import { useCallback, useContext, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router";

import {
  Button,
  Buttons,
  Color,
  Icon,
  Link,
  LoadingGroup,
  LoadingSkeleton,
  Message,
  Messages,
  Panel,
  Tag,
  TagType,
} from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Bold, Heading, Semibold, Subtitle, TextColor } from "capitalrx-components-2.0/lib/typography";
import { CaseTags } from "capitalrx-types";
import { formatDate } from "capitalrx-utilities";

import {
  PriorAuthorizationDecisionSubLetter,
  getPriorAuthorizationDocumentsUrl,
  useGetPriorAuthorizationCaseQuery,
  useListPriorAuthorizationDecisionLetter,
} from "@/App/api/priorAuthorization";
import { AppleMap } from "capitalrx-components-2.0";
import { ContentPanel, NoResults, PageWrapper, ThemedButton } from "@/App/components";
import { StackOrShelf } from "@/App/components/StackOrShelf";
import { SearchParamKeys } from "@/App/constants/constants";
import { AuthenticationContext, ExperienceContext, MemberEligibilityContext } from "@/App/context";
import { useNavigateClientNewTab } from "@/App/hooks";
import { RenewalRequestFormModal } from "@/App/pages/PriorAuthorization/RenewalRequestFormModal";
import { enablePriorAuthorizationRenewalRequest } from "@/App/utils/featureFlags";
import { formatPhoneNumber, getCustomerSupportNumber } from "@/App/utils/utils";
import { ReactNode } from "react";

const TRANSLATION_PATH = "PRIOR_AUTHORIZATION.priorAuthorizationDetailsPage";

const InfoStack = ({ title, body }: { title: string; body: ReactNode | string }) => {
  return (
    <Stack spacing="mini" alignment="left">
      <Semibold><TextColor appearance={Color.DarkGrey["400"]}>{title}</TextColor></Semibold>
      <Body>{body}</Body>
    </Stack>
  );
};

export const PriorAuthorizationDetailsPage = () => {
  const { t: translate } = useTranslation();
  const navigateNewTab = useNavigateClientNewTab();
  const location = useLocation();
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { priorAuthorizationPhoneNumber } = useContext(ExperienceContext);
  const customerSupportNumber = priorAuthorizationPhoneNumber ? priorAuthorizationPhoneNumber : getCustomerSupportNumber(memberEligibility);
  const { priorAuthorizationId } = useParams();
  const [renewalFormIsVisible, setRenewalFormIsVisible] = useState(false);
  const memberCameFromHomePage = location.state?.previousPage === "home";

  const {
    data: priorAuthorizationCaseData,
    isLoading: paCaseIsLoading,
    isLoadingError: isLoadingPACaseError,
    isRefetching: paCaseIsRefetching,
    isError: isPAFetchingError,
    refetch: refetchPaCase,
  } = useGetPriorAuthorizationCaseQuery({ id: +priorAuthorizationId });
  const { getAuthToken } = useContext(AuthenticationContext);

  const {
    data: documentListData,
    isLoading: isLoadingDocument,
    isError: isErrorDocument,
  } = useListPriorAuthorizationDecisionLetter({ case_id: +priorAuthorizationId });

  const caseOutcomesObject = {
    approved: [translate(`${TRANSLATION_PATH}.approved`), "success-high-contrast"],
    denied: [translate(`${TRANSLATION_PATH}.denied`), "error-high-contrast"],
    closed: [translate(`${TRANSLATION_PATH}.closed`), "info-high-contrast"],
    in_progress: [translate(`${TRANSLATION_PATH}.inProgress`), "warning-high-contrast"],
  };
  const caseOutcome = priorAuthorizationCaseData?.case_outcome?.outcome || "in_progress";

  const dateFormat = "MM/DD/YYYY";

  const formattedPrescriberPhoneNumber = useMemo(() => {
    return formatPhoneNumber(priorAuthorizationCaseData?.prescriber_business_practice_phone);
  }, [priorAuthorizationCaseData?.prescriber_business_practice_phone]);

  const prescriberLocation = priorAuthorizationCaseData?.prescriber_practice_location;

  const prescriberDisplayName = useMemo(() => {
    const firstName = priorAuthorizationCaseData?.prescriber_first_name ?? "";
    const lastName = priorAuthorizationCaseData?.prescriber_last_name ?? "";
    const name = `${firstName} ${lastName}`.trim();
    const credential = prescriberLocation?.credential;
    return credential ? `${name}, ${credential}` : name;
  }, [priorAuthorizationCaseData?.prescriber_first_name, priorAuthorizationCaseData?.prescriber_last_name, prescriberLocation?.credential]);

  const prescriberAddressLines = useMemo(() => {
    if (!prescriberLocation) return [];
    const lines: string[] = [];
    if (prescriberLocation.address_line_1) lines.push(prescriberLocation.address_line_1);
    if (prescriberLocation.address_line_2) lines.push(prescriberLocation.address_line_2);
    if (prescriberLocation.city && prescriberLocation.state && prescriberLocation.zip_code) {
      lines.push(`${prescriberLocation.city}, ${prescriberLocation.state} ${prescriberLocation.zip_code}`);
    }
    return lines;
  }, [prescriberLocation]);

  const prescriberMapQuery = useMemo(() => {
    if (!prescriberLocation?.address_line_1) return undefined;
    const query = [
      prescriberLocation.address_line_1,
      prescriberLocation.city,
      prescriberLocation.state,
      prescriberLocation.zip_code,
    ].filter(Boolean).join(", ");
    return { query, title: prescriberDisplayName };
  }, [prescriberLocation, prescriberDisplayName]);

  const handlePrintPage = () => {
    const searchParams = new URLSearchParams();
    searchParams.append("pa_case_id", priorAuthorizationId);
    if (isViewingDataholder) {
      searchParams.append(SearchParamKeys.MEMBER_ELIGIBILITY_MEMBER_ID, memberEligibility.member.id.toString());
    }
    navigateNewTab("/print-prior-authorization-page", searchParams);
  };

  const handlePrintPriorAuthorizationDocument = useCallback(
    async (pa_letter: PriorAuthorizationDecisionSubLetter) => {
      const { url } = await getPriorAuthorizationDocumentsUrl(
        await getAuthToken(),
        {
          id: pa_letter.case_id,
          uuid: pa_letter.uuid,
        },
        memberEligibility,
        isViewingDataholder,
      );

      window.open(url);
    },
    [memberEligibility, isViewingDataholder],
  );

  const { isCompressed, cardColumns, stackColumns } = useResponsive({
    isCompressed: {
      desktop: false,
      tablet: false,
      mobile: true,
    },
    cardColumns: {
      desktop: 6,
      tablet: 6,
      mobile: 12,
    },
    stackColumns: {
      desktop: 6,
      tablet: 12,
      mobile: 12,
    },
  });

  const isDataReady = !isPAFetchingError && !isLoadingPACaseError && !paCaseIsLoading && !paCaseIsRefetching;

  const renderAuthorizationSummaryContent = () => {
    if (caseOutcome === "approved") {
      return (
        <Grid>
          <Grid.Span columns={stackColumns}>
            <InfoStack title={translate(`${TRANSLATION_PATH}.patient`)} body={`${priorAuthorizationCaseData?.member_first_name ?? ""} ${priorAuthorizationCaseData?.member_last_name ?? ""}`.trim()} />
          </Grid.Span>
          <Grid.Span columns={stackColumns}>
            <InfoStack title={translate(`${TRANSLATION_PATH}.id`)} body={priorAuthorizationCaseData?.case_id} />
          </Grid.Span>
          <Grid.Span columns={stackColumns}>
            <InfoStack title={translate(`${TRANSLATION_PATH}.approved`)} body={formatDate(priorAuthorizationCaseData?.completed_at, dateFormat, true)} />
          </Grid.Span>
          <Grid.Span columns={stackColumns}>
            <InfoStack title={translate(`${TRANSLATION_PATH}.coverageStarts`)} body={formatDate(priorAuthorizationCaseData?.prior_authorization?.effective_date, dateFormat)} />
          </Grid.Span>
          <Grid.Span columns={stackColumns}>
            <InfoStack title={translate(`${TRANSLATION_PATH}.coverageExpires`)} body={formatDate(priorAuthorizationCaseData?.prior_authorization?.expiration_date, dateFormat)} />
          </Grid.Span>
          {priorAuthorizationCaseData?.is_renewal_available && priorAuthorizationCaseData?.renewal_start_date && (
            <Grid.Span columns={stackColumns}>
              <InfoStack title={translate(`${TRANSLATION_PATH}.renewalAvailable`)} body={formatDate(priorAuthorizationCaseData?.renewal_start_date, dateFormat)} />
            </Grid.Span>
          )}
        </Grid>
      );
    }

    if (caseOutcome === "denied") {
      return (
        <Grid>
          <Grid.Span columns={stackColumns}>
            <InfoStack title={translate(`${TRANSLATION_PATH}.patient`)} body={`${priorAuthorizationCaseData?.member_first_name ?? ""} ${priorAuthorizationCaseData?.member_last_name ?? ""}`.trim()} />
          </Grid.Span>
          <Grid.Span columns={stackColumns}>
            <InfoStack title={translate(`${TRANSLATION_PATH}.id`)} body={priorAuthorizationCaseData?.case_id} />
          </Grid.Span>
          <Grid.Span columns={stackColumns}>
            <InfoStack title={translate(`${TRANSLATION_PATH}.requested`)} body={formatDate(priorAuthorizationCaseData?.received_at, dateFormat, true)} />
          </Grid.Span>
          <Grid.Span columns={stackColumns}>
            <InfoStack title={translate(`${TRANSLATION_PATH}.denied`)} body={formatDate(priorAuthorizationCaseData?.completed_at, dateFormat, true)} />
          </Grid.Span>
          {priorAuthorizationCaseData?.case_outcome?.denial_type != null && (
            <Grid.Span columns={stackColumns}>
              <InfoStack title={translate(`${TRANSLATION_PATH}.denialType`)} body={<Body testId="denialType">{translate(`${TRANSLATION_PATH}.denialTypes.${priorAuthorizationCaseData?.case_outcome?.denial_type}`)}</Body>} />
            </Grid.Span>
          )}
          {priorAuthorizationCaseData?.case_outcome?.denial_reason != null && (
            <Grid.Span columns={stackColumns}>
              <InfoStack title={translate(`${TRANSLATION_PATH}.denialReason`)} body={<Body testId="denialReason">{translate(`${TRANSLATION_PATH}.denialReasons.${priorAuthorizationCaseData?.case_outcome?.denial_reason}`)}</Body>} />
            </Grid.Span>
          )}
        </Grid>
      );
    }

    if (caseOutcome === "closed") {
      return (
        <Grid>
          <Grid.Span columns={stackColumns}>
            <InfoStack title={translate(`${TRANSLATION_PATH}.patient`)} body={`${priorAuthorizationCaseData?.member_first_name ?? ""} ${priorAuthorizationCaseData?.member_last_name ?? ""}`.trim()} />
          </Grid.Span>
          <Grid.Span columns={stackColumns}>
            <InfoStack title={translate(`${TRANSLATION_PATH}.id`)} body={priorAuthorizationCaseData?.case_id} />
          </Grid.Span>
          <Grid.Span columns={stackColumns}>
            <InfoStack title={translate(`${TRANSLATION_PATH}.closed`)} body={formatDate(priorAuthorizationCaseData?.completed_at, dateFormat, true)} />
          </Grid.Span>
        </Grid>
      );
    }

    // in_progress
    return (
      <Grid>
        <Grid.Span columns={stackColumns}>
          <InfoStack title={translate(`${TRANSLATION_PATH}.patient`)} body={`${priorAuthorizationCaseData?.member_first_name ?? ""} ${priorAuthorizationCaseData?.member_last_name ?? ""}`.trim()} />
        </Grid.Span>
        <Grid.Span columns={stackColumns}>
          <InfoStack title={translate(`${TRANSLATION_PATH}.id`)} body={priorAuthorizationCaseData?.case_id} />
        </Grid.Span>
      </Grid>
    );
  };

  return (
    <>
      {enablePriorAuthorizationRenewalRequest() && (
        <RenewalRequestFormModal
          onClose={() => setRenewalFormIsVisible(null)}
          isVisible={renewalFormIsVisible}
          caseId={priorAuthorizationCaseData?.case_id}
          refresh={refetchPaCase}
        />
      )}
      <PageWrapper>
        <ContentPanel
          title={
            <Stack spacing="large">
              <Stack>
                {/* @ts-ignore */}
                <StackOrShelf alignment={isCompressed ? "left" : "top"}>
                  <Stack spacing="mini" alignment="left">
                    <Shelf spacing="mini">
                      <Icon name="arrow-left" color="turquoise" />
                      {!memberCameFromHomePage && <Link to={"/prior-authorization"}>{translate(`${TRANSLATION_PATH}.priorAuthorization`)}</Link>}
                      {memberCameFromHomePage && <Link to={"/"}>{translate("MENU_BAR.home")}</Link>}
                    </Shelf>
                    <LoadingGroup loading={paCaseIsLoading || paCaseIsRefetching}>
                      <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ height: 36, width: 120 }} />}>
                        {isDataReady && (
                          <Stack spacing="mini">
                            <Shelf alignment="center" spacing="medium">
                              <Subtitle testId="prior-authorization-drug-name">{priorAuthorizationCaseData?.drug_name}</Subtitle>
                              {priorAuthorizationCaseData?.tags.includes(CaseTags.is_awaiting_prescriber_response)
                              && caseOutcome == "in_progress" ? (
                                <Tag type="warning-high-contrast">{translate("PRIOR_AUTHORIZATION.awaitingResponse")}</Tag>
                              ) : (
                                <Tag testId="case-outcome" type={caseOutcomesObject[caseOutcome]?.[1] as TagType}>
                                  {caseOutcomesObject[caseOutcome]?.[0]}
                                </Tag>
                              )}
                              {priorAuthorizationCaseData?.is_renewal_available && (
                                <Tag type="system-alternative-high-contrast">{translate(`${TRANSLATION_PATH}.renewalAvailable`)}</Tag>
                              )}
                              {priorAuthorizationCaseData?.next_case_review_level === "renewal" && (
                                <Tag type="info">{translate(`${TRANSLATION_PATH}.renewalRequested`)}</Tag>
                              )}
                              <Shelf.Spacer />
                            </Shelf>
                            <Body><Semibold><TextColor appearance={Color.DarkGrey[400]}>{prescriberDisplayName}</TextColor></Semibold></Body>
                            <StackOrShelf spacing="large" alignment="left">
                              <Shelf spacing="mini">
                                <Body><Semibold><TextColor appearance={Color.DarkGrey[400]}>{translate(`${TRANSLATION_PATH}.id`)}:</TextColor></Semibold></Body>
                                <Body testId="case-id">{priorAuthorizationCaseData?.case_id}</Body>
                              </Shelf>
                              <Shelf spacing="mini">
                                <Body><Semibold><TextColor appearance={Color.DarkGrey[400]}>{translate(`${TRANSLATION_PATH}.requested`)}:</TextColor></Semibold></Body>
                                <Body>{formatDate(priorAuthorizationCaseData?.received_at, dateFormat, true)}</Body>
                              </Shelf>
                              <Shelf.Spacer />
                            </StackOrShelf>
                          </Stack>
                        )}
                      </LoadingGroup.Placeholder>
                    </LoadingGroup>
                  </Stack>
                  <Shelf.Spacer />
                  {isDataReady && (
                    <Button appearance="text" icon="print" onClick={handlePrintPage}>
                      {translate(`${TRANSLATION_PATH}.printPage`)}
                    </Button>
                  )}
                </StackOrShelf>
              </Stack>
              {isDataReady
                && caseOutcome == "in_progress"
                && priorAuthorizationCaseData?.tags.includes(CaseTags.is_awaiting_prescriber_response) && (
                  <Message appearance={Messages.Informational} visible>
                    <Body>{translate(`${TRANSLATION_PATH}.additionalInformationAndDocumentation`)}</Body>
                  </Message>
                )}
            </Stack>
          }
          showBorder={false}
        >
          <Stack>
            {enablePriorAuthorizationRenewalRequest() && (
              <Message appearance={Messages.Informational} visible={priorAuthorizationCaseData?.is_renewal_available}>
                <StackOrShelf>
                  <Body>
                    <Trans
                      i18nKey={`${TRANSLATION_PATH}.authorizationRequestExpiresMessage`}
                      values={{
                        drugName: priorAuthorizationCaseData?.drug_name ?? "",
                        date: formatDate(priorAuthorizationCaseData?.prior_authorization?.expiration_date, "MM/DD/YYYY") ?? "",
                      }}
                      components={{ bold: <Bold /> }}
                    />
                  </Body>
                  <Shelf.Spacer />
                  <Shelf>
                    <ThemedButton style={{ padding: "0" }} appearance="text" onClick={() => setRenewalFormIsVisible(true)}>
                      {translate(`${TRANSLATION_PATH}.submitRenewalRequest`)}
                    </ThemedButton>
                    <Shelf.Spacer />
                  </Shelf>
                </StackOrShelf>
              </Message>
            )}
            <LoadingGroup loading={paCaseIsLoading || paCaseIsRefetching}>
              <LoadingGroup.Placeholder fallback={<PADetailsSkeleton />}>
                {isDataReady && (
                  <Grid>
                    {/* Left Column */}
                    <Grid.Span columns={cardColumns}>
                      <Stack spacing="medium">
                        {/* Authorization Summary Card */}
                        <Stack spacing="medium">
                          <Heading>{translate(`${TRANSLATION_PATH}.authorizationSummary`)}</Heading>
                          <Panel appearance="primary" className="member-portal-panel">
                            <Stack spacing="medium">
                              {renderAuthorizationSummaryContent()}
                              {!isLoadingDocument && !isErrorDocument && documentListData?.results
                                && +documentListData?.result_count > 0 && (
                                <Stack spacing="small" alignment="left">
                                  {documentListData.results.map((pa_document) => (
                                    <Button
                                      key={pa_document.letter.uuid}
                                      appearance={Buttons.Functional.Tertiary.Compact}
                                      icon="link-out"
                                      iconPosition="right"
                                      onClick={() => handlePrintPriorAuthorizationDocument(pa_document.letter)}
                                    >
                                      {pa_document.letter.filename}
                                    </Button>
                                  ))}
                                </Stack>
                              )}
                            </Stack>
                          </Panel>
                        </Stack>

                        {/* Prescription Details Card */}
                        <Stack spacing="medium">
                          <Heading>{translate(`${TRANSLATION_PATH}.prescriptionDetails`)}</Heading>
                          <Panel appearance="primary" className="member-portal-panel">
                            <Stack spacing="small">
                              <Heading testId="drug-name">{priorAuthorizationCaseData?.drug_name}</Heading>
                              <Grid>
                                <Grid.Span columns={stackColumns}>
                                  <InfoStack title={translate(`${TRANSLATION_PATH}.quantity`)} body={<Body testId="quantity">{priorAuthorizationCaseData?.drug_quantity}</Body>} />
                                </Grid.Span>
                                <Grid.Span columns={stackColumns}>
                                  <InfoStack title={translate(`${TRANSLATION_PATH}.daysSupply`)} body={<Body testId="days_supply">{priorAuthorizationCaseData?.drug_days_supply}</Body>} />
                                </Grid.Span>
                                <Grid.Span columns={stackColumns}>
                                  <InfoStack title={translate(`${TRANSLATION_PATH}.dosage`)} body={priorAuthorizationCaseData?.drug_dosage} />
                                </Grid.Span>
                                <Grid.Span columns={stackColumns}>
                                  <InfoStack title={translate(`${TRANSLATION_PATH}.form`)} body={priorAuthorizationCaseData?.drug_dosage_form} />
                                </Grid.Span>
                              </Grid>
                              {priorAuthorizationCaseData?.drug_directions_for_use && (
                                <InfoStack title={translate(`${TRANSLATION_PATH}.directionsForUse`)} body={priorAuthorizationCaseData?.drug_directions_for_use} />
                              )}
                            </Stack>
                          </Panel>
                        </Stack>
                        <Shelf spacing="mini">
                          <Icon name="phone" />
                          <Body>
                            {translate(`${TRANSLATION_PATH}.contactCustomerCareAt`)}{" "}
                            <a href={`tel:${customerSupportNumber}`}>{customerSupportNumber}</a>.
                          </Body>
                          <Shelf.Spacer />
                        </Shelf>
                      </Stack>
                    </Grid.Span>

                    {/* Right Column */}
                    <Grid.Span columns={cardColumns}>
                      <Stack spacing="medium">
                        <Heading>{translate(`${TRANSLATION_PATH}.prescriberDetails`)}</Heading>
                        <Panel appearance="primary" className="member-portal-panel">
                          <Stack spacing="medium">
                            <InfoStack title={translate(`${TRANSLATION_PATH}.prescriberName`)} body={<Body testId="prescriber-name" style={{ textTransform: "uppercase" }}>{prescriberDisplayName}</Body>} />
                            <Grid>
                              <Grid.Span columns={stackColumns}>
                                <InfoStack title={translate(`${TRANSLATION_PATH}.npi`)} body={<Body testId="prescriber-npi">{priorAuthorizationCaseData?.prescriber_npi}</Body>} />
                              </Grid.Span>
                              <Grid.Span columns={stackColumns}>
                                <InfoStack
                                  title={translate(`${TRANSLATION_PATH}.phone`)}
                                  body={
                                    formattedPrescriberPhoneNumber
                                      ? <a href={`tel:${formattedPrescriberPhoneNumber}`}>{formattedPrescriberPhoneNumber}</a>
                                      : "-"
                                  }
                                />
                              </Grid.Span>
                            </Grid>
                            {prescriberAddressLines.length > 0 && (
                              <InfoStack
                                title={translate(`${TRANSLATION_PATH}.address`)}
                                body={
                                  <Stack spacing="none">
                                    {prescriberAddressLines.map((line, idx) => (
                                      <Body key={idx}>{line}</Body>
                                    ))}
                                  </Stack>
                                }
                              />
                            )}
                          </Stack>
                          {prescriberMapQuery && (
                            <div style={{ paddingTop: "32px" }}>
                              <AppleMap
                                style={{ height: "250px" }}
                                query={prescriberMapQuery}
                              />
                            </div>
                          )}
                        </Panel>
                      </Stack>
                    </Grid.Span>
                  </Grid>
                )}
                {(isPAFetchingError || isLoadingPACaseError) && <NoResults message={translate(`${TRANSLATION_PATH}.anErrorOccurred`)} />}
              </LoadingGroup.Placeholder>
            </LoadingGroup>

          </Stack>
        </ContentPanel>
      </PageWrapper>
    </>
  );
};

const PADetailsSkeleton = () => {
  return (
    <Grid>
      <Grid.Span columns={6}>
        <Stack spacing="large">
          <Stack>
            <LoadingSkeleton style={{ height: "24px", width: "150px" }} />
            <Panel appearance="primary" className="member-portal-panel">
              <Stack>
                <Stack spacing="mini" alignment="left">
                  <LoadingSkeleton style={{ height: "24px", width: "100px" }} />
                  <LoadingSkeleton style={{ height: "20px", width: "250px" }} />
                </Stack>
                <Stack spacing="mini" alignment="left">
                  <LoadingSkeleton style={{ height: "24px", width: "100px" }} />
                  <LoadingSkeleton style={{ height: "20px", width: "250px" }} />
                </Stack>
                <Stack spacing="mini" alignment="left">
                  <LoadingSkeleton style={{ height: "24px", width: "75px" }} />
                  <LoadingSkeleton style={{ height: "20px", width: "100px" }} />
                </Stack>
              </Stack>
            </Panel>
          </Stack>
          <Stack>
            <LoadingSkeleton style={{ height: "24px", width: "150px" }} />
            <Panel appearance="primary" className="member-portal-panel">
              <Stack spacing="mini">
                <LoadingSkeleton style={{ height: "20px", width: "250px", maxWidth: "100%" }} />
                <LoadingSkeleton style={{ height: "20px", width: "250px", maxWidth: "100%" }} />
                <LoadingSkeleton style={{ height: "20px", width: "200px", maxWidth: "100%" }} />
              </Stack>
            </Panel>
          </Stack>
        </Stack>
      </Grid.Span>
      <Grid.Span columns={6}>
        <Stack>
          <LoadingSkeleton style={{ height: "24px", width: "150px" }} />
          <Panel appearance="primary" className="member-portal-panel">
            <Grid>
              <Grid.Span columns={12}>
                <LoadingSkeleton style={{ height: "24px", width: "175px", maxWidth: "100%" }} />
              </Grid.Span>
              <Grid.Span columns={6}>
                <LoadingSkeleton style={{ height: "20px", width: "100px", maxWidth: "75%" }} />
                <LoadingSkeleton style={{ height: "24px", width: "175px", maxWidth: "100%" }} />
              </Grid.Span>
              <Grid.Span columns={6}>
                <LoadingSkeleton style={{ height: "20px", width: "100px", maxWidth: "75%" }} />
                <LoadingSkeleton style={{ height: "24px", width: "175px", maxWidth: "100%" }} />
              </Grid.Span>
              <Grid.Span columns={12}>
                <LoadingSkeleton style={{ height: "250px", width: "100%" }} />
              </Grid.Span>
            </Grid>
          </Panel>
        </Stack>
      </Grid.Span>
    </Grid>
  );
};
